import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Button, Empty, Input, Select, Row, Col, Space } from 'antd';
import { PlusOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { formatDate, formatMontant } from '../../variables/constants';
import { listerCommandes, STATUTS_COMMANDE } from '../../services/attestationsService';
import TableExportButton from '../../components/TableExportButton';

import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';

const { Search } = Input;
const { Option } = Select;

export default function CommandesTab({ onOpenDetail, onCreate, refreshKey, actif }) {
  const { t } = useTranslation('attestations');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');

  const load = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const res = await listerCommandes(page, 20);
      setData(res.items);
      setPagination(prev => ({ ...prev, current: res.currentPage + 1, pageSize: res.pageSize, total: res.totalElements }));
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0); }, [load, refreshKey]);

  const filtered = data.filter(c =>
    (!search ||
      c.numeroCommande?.toLowerCase().includes(search.toLowerCase()) ||
      c.organismeRaisonSociale?.toLowerCase().includes(search.toLowerCase()))
    && (!filtreStatut || c.statut === filtreStatut)
  );

  const columns = [
    { title: t('commandes.colonnes.numero'), dataIndex: 'numeroCommande', key: 'num', width: 150, fixed: 'left',
      render: (v, r) => (
        <button onClick={() => onOpenDetail?.(r)}
          style={{ background: 'none', border: 'none', color: '#1a6b3c', fontWeight: 700,
            fontFamily: 'var(--font-main)', cursor: 'pointer', padding: 0, fontSize: 13 }}>
          {v}
        </button>
      ) },
    { title: t('commun.colonnes.compagnie'), dataIndex: 'organismeRaisonSociale', key: 'org', ellipsis: true },
    { title: t('commun.colonnes.quantite'), dataIndex: 'quantite', key: 'qty', width: 100, align: 'right',
      render: (v) => <strong>{new Intl.NumberFormat(localeIntl()).format(v)}</strong> },
    { title: t('commun.colonnes.total'), dataIndex: 'montantTotal', key: 'tot', width: 150, align: 'right', format: 'currency', numeric: true,
      render: (v) => <strong style={{ color: '#1a6b3c' }}>{formatMontant(v)}</strong> },
    { title: t('commun.colonnes.date'), dataIndex: 'dateCommande', key: 'dc', width: 100, format: 'date', render: formatDate },
    { title: t('commandes.colonnes.livraison'), dataIndex: 'dateLivraisonEffective', key: 'dl', width: 100, format: 'date',
      render: (v) => v ? formatDate(v) : '-' },
    { title: t('commun.colonnes.statut'), dataIndex: 'statut', key: 'st', width: 140,
      export: (v) => (STATUTS_COMMANDE[v]?.label) || v || '',
      render: (v) => {
        const s = STATUTS_COMMANDE[v] || { label: v, color: 'default' };
        return <Tag color={s.color} style={{ borderRadius: 20 }}>{s.label}</Tag>;
      } },
    { title: '', key: 'act', width: 90, fixed: 'right', exportable: false,
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => onOpenDetail?.(r)}>{t('commandes.detail')}</Button>
      ) },
  ];

  return (
    <>
      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
        <Col xs={24} md={10}>
          <Search placeholder={t('commandes.recherche')} value={search}
            onChange={(e) => setSearch(e.target.value)} allowClear />
        </Col>
        <Col xs={12} md={6}>
          <Select placeholder={t('commun.tousStatuts')} value={filtreStatut || undefined}
            allowClear style={{ width: '100%' }} onChange={(v) => setFiltreStatut(v || '')}>
            {Object.entries(STATUTS_COMMANDE).map(([k, s]) => (
              <Option key={k} value={k}><Tag color={s.color}>{s.label}</Tag></Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => load(pagination.current - 1)}>{t('commun.actualiser')}</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
              {t('commandes.nouvelle')}
            </Button>
          </Space>
        </Col>
      </Row>

      <Table className="cb-table" dataSource={filtered} columns={columns} rowKey="commandeTrackingId"
        loading={loading} scroll={{ x: 1100 }}
        pagination={{
          current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
          onChange: (page) => load(page - 1),
          showTotal: (total) => t('commandes.total', { total }),
        }}
        locale={{ emptyText: <Empty description={t('commandes.vide')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />

      {/*  Export flottant  */}
      {/* RENDU SEULEMENT SI CET ONGLET EST ACTIF. Les quatre onglets de l'ecran
          portent chacun leur export ; AntD garde montes les volets deja visites, et
          quatre boutons flottants se seraient superposes au meme coin. La condition
          est ce qui garantit qu'il n'y en a qu'un.

          `floating` suffit au placement : le composant pose lui-meme l'enveloppe et
          la reserve de place en bas de page. */}
      {actif && (
        <TableExportButton
          data={filtered}
          columns={columns}
          filename="attestations-commandes"
          title={t('commandes.exportTitre')}
          subtitle={[search && t('commun.recherche', { search }), filtreStatut && t('commun.statutFiltre', { statut: STATUTS_COMMANDE[filtreStatut]?.label || filtreStatut })].filter(Boolean).join(' - ') || undefined}
          floating
        />
      )}
    </>
  );
}
